/**
 * Custom hook for fetching wallet transactions with pagination and filtering
 * @module hooks/use-transactions
 */

import { useState, useEffect, useCallback } from 'react';
import { getTransactions } from '@/lib/api/wallet';
import type {
  Transaction,
  TransactionQueryParams,
  TransactionType,
  TransactionStatus,
} from '@/types/wallet';
import type { PaginationMeta } from '@/types/api';

export interface UseTransactionsParams {
  initialPage?: number;
  initialPerPage?: number;
  initialType?: TransactionType;
  initialStatus?: TransactionStatus;
  initialDateFrom?: string;
  initialDateTo?: string;
}

export interface UseTransactionsReturn {
  data: Transaction[];
  loading: boolean;
  error: string | null;
  meta: PaginationMeta | null;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setType: (type: TransactionType | undefined) => void;
  setStatus: (status: TransactionStatus | undefined) => void;
  setDateRange: (from?: string, to?: string) => void;
  currentParams: TransactionQueryParams;
}

/**
 * Custom hook for fetching paginated and filtered transactions
 *
 * @example
 * const { data, loading, error, meta, setPage, setType } = useTransactions();
 * // Change page
 * setPage(2);
 * // Filter by type
 * setType('Fund');
 */
export function useTransactions(
  params: UseTransactionsParams = {}
): UseTransactionsReturn {
  const [data, setData] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);

  const [currentParams, setCurrentParams] = useState<TransactionQueryParams>({
    page: params.initialPage || 1,
    per_page: params.initialPerPage || 10,
    type: params.initialType,
    status: params.initialStatus,
    from_date: params.initialDateFrom,
    to_date: params.initialDateTo,
  });

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getTransactions(currentParams);

      if (response.success && response.data) {
        setData(response.data);
        setMeta(response.meta || null);
      } else {
        setError(response.message || 'Failed to load transactions');
        setData([]);
        setMeta(null);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred'
      );
      setData([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [currentParams]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const setPage = useCallback((page: number) => {
    setCurrentParams((prev) => ({ ...prev, page }));
  }, []);

  const setPerPage = useCallback((perPage: number) => {
    setCurrentParams((prev) => ({ ...prev, per_page: perPage, page: 1 }));
  }, []);

  const setType = useCallback((type: TransactionType | undefined) => {
    setCurrentParams((prev) => ({ ...prev, type, page: 1 }));
  }, []);

  const setStatus = useCallback((status: TransactionStatus | undefined) => {
    setCurrentParams((prev) => ({ ...prev, status, page: 1 }));
  }, []);

  const setDateRange = useCallback((from?: string, to?: string) => {
    setCurrentParams((prev) => ({
      ...prev,
      from_date: from,
      to_date: to,
      page: 1,
    }));
  }, []);

  return {
    data,
    loading,
    error,
    meta,
    refetch: fetchTransactions,
    setPage,
    setPerPage,
    setType,
    setStatus,
    setDateRange,
    currentParams,
  };
}
